// AgentRuntime.js
// Generalized realtime agent session for Comprehend Live.
// Modeled on components/Misc/Synapse/OpenAIAssistant.js (WebRTC + ephemeral key
// from /v1/misc/synapse/key) but adds: text in/out over the data channel,
// user/assistant transcript events for the chat UI, proper function_call_output
// round-trips, and text-only replies while a patient visit is recording.
import { Emitter } from './events';
import { ToolsRegistry } from './ToolRegistry';
import { ContextRegistry } from './ContextRegistry';
import {
  COMPREHEND_LIVE_DIRECTIVE,
  VERBOSITY_RULES,
  LIVE_MODEL,
  LIVE_VOICE,
} from './prompts';

export class AgentRuntime {
  constructor(apiClient, options = {}) {
    this.apiClient = apiClient;
    this.deviceId = options.deviceId || null;

    this.events = new Emitter();
    this.toolsRegistry = new ToolsRegistry();
    this.contextRegistry = new ContextRegistry();

    this.verbosity = options.verbosity || 'normal';
    this.systemDirective =
      (options.systemDirective || COMPREHEND_LIVE_DIRECTIVE) +
      (VERBOSITY_RULES[this.verbosity] || '');
    this.model = options.model || LIVE_MODEL;
    this.voice = options.voice || LIVE_VOICE;

    // User preference for spoken replies; recording state can override it.
    this.voiceRepliesPreferred = options.voiceReplies !== false;
    this.recordingActive = false;

    this.status = 'idle'; // idle | connecting | ready | listening | error
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.mediaStream = null;
    this.ephemeralKey = null;

    // Accumulates streamed assistant text per response id.
    this.pendingResponses = new Map();
  }

  on(event, cb) {
    return this.events.on(event, cb);
  }

  voiceRepliesActive() {
    // The user's explicit preference rules. Recording AUTO-MUTES the
    // preference once when it starts (see useComprehendLive), but the
    // clinician can re-enable voice mid-recording — it's a default, not
    // a lock (a hard lock made the toggle appear dead).
    return this.voiceRepliesPreferred;
  }

  setStatus(status) {
    this.status = status;
    this.events.emit('status', status);
  }

  /**
   * Establish the realtime session. Safe to call once; subsequent calls no-op
   * while a session is active.
   */
  async init() {
    if (this.status === 'connecting' || this.status === 'ready') return;
    this.setStatus('connecting');
    try {
      if (!this.ephemeralKey) {
        const keyResponse =
          await this.apiClient.getSynapseKey('openai_realtime');
        if (!keyResponse || !keyResponse.accessKey) {
          throw new Error('No ephemeral key returned from backend');
        }
        this.ephemeralKey = keyResponse.accessKey;
      }

      this.peerConnection = new RTCPeerConnection();
      this.audioElement = document.createElement('audio');
      this.audioElement.autoplay = true;
      this.peerConnection.ontrack = (event) => {
        this.audioElement.srcObject = event.streams[0];
      };

      this.dataChannel = this.peerConnection.createDataChannel('oai-events');
      this.dataChannel.onmessage = (event) => {
        try {
          this.processIncomingEvent(JSON.parse(event.data));
        } catch (err) {
          console.error('AgentRuntime: failed to parse server event', err);
        }
      };

      const audioConstraints = this.deviceId
        ? { deviceId: { exact: this.deviceId } }
        : true;
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
      const audioTrack = this.mediaStream.getAudioTracks()[0];
      audioTrack.enabled = false; // muted until push-to-talk / wake word
      this.peerConnection.addTrack(audioTrack, this.mediaStream);

      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      const sdpResponse = await fetch(
        'https://api.openai.com/v1/realtime/calls',
        {
          method: 'POST',
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${this.ephemeralKey}`,
            'Content-Type': 'application/sdp',
          },
        },
      );
      if (!sdpResponse.ok) {
        throw new Error(
          `Realtime SDP handshake failed (${sdpResponse.status})`,
        );
      }
      const answerSdp = await sdpResponse.text();
      await this.peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });

      await new Promise((resolve) => {
        if (this.dataChannel.readyState === 'open') resolve();
        else this.dataChannel.onopen = () => resolve();
      });

      this.sendClientEvent(this.getSessionUpdatePayload());
      this.setStatus('ready');
    } catch (error) {
      console.error('AgentRuntime init failed:', error);
      this.ephemeralKey = null; // force re-fetch on retry
      this.setStatus('error');
      this.events.emit('error', error);
    }
  }

  destroy() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.ephemeralKey = null;
    this.pendingResponses.clear();
    this.setStatus('idle');
  }

  /* ===== Mic gating (push-to-talk / wake word) ===== */

  startListening() {
    const track = this.mediaStream?.getAudioTracks?.()[0];
    if (track) {
      track.enabled = true;
      this.setStatus('listening');
    }
  }

  stopListening() {
    const track = this.mediaStream?.getAudioTracks?.()[0];
    if (track) track.enabled = false;
    if (this.status === 'listening') this.setStatus('ready');
  }

  /* ===== Modality control ===== */

  setVoiceRepliesPreferred(enabled) {
    this.voiceRepliesPreferred = Boolean(enabled);
    this.pushSessionUpdate();
  }

  /** Response style (brief | normal | wordy) — updates live. */
  setVerbosity(level) {
    this.verbosity = VERBOSITY_RULES[level] !== undefined ? level : 'normal';
    this.systemDirective =
      COMPREHEND_LIVE_DIRECTIVE + (VERBOSITY_RULES[this.verbosity] || '');
    this.pushSessionUpdate();
  }

  /** Switch microphones mid-session via replaceTrack. */
  async setDevice(deviceId) {
    this.deviceId = deviceId || null;
    if (!this.peerConnection) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: this.deviceId ? { deviceId: { exact: this.deviceId } } : true,
      });
      const track = stream.getAudioTracks()[0];
      track.enabled = this.status === 'listening';
      const sender = this.peerConnection
        .getSenders()
        .find((sn) => sn.track?.kind === 'audio');
      if (sender) await sender.replaceTrack(track);
      this.mediaStream?.getTracks().forEach((t) => t.stop());
      this.mediaStream = stream;
    } catch (error) {
      console.error('Mic switch failed:', error);
    }
  }

  /**
   * Called by the live store whenever any visit recording starts/stops.
   * While recording, replies are forced to text so the agent never talks
   * over a patient encounter.
   */
  setRecordingActive(active) {
    if (this.recordingActive === Boolean(active)) return;
    this.recordingActive = Boolean(active);
    this.pushSessionUpdate();
    this.events.emit('voice_replies', this.voiceRepliesActive());
  }

  /* ===== Messaging ===== */

  sendUserText(text) {
    if (!text || !text.trim()) return;
    this.sendClientEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: text.trim() }],
      },
    });
    this.createResponse();
  }

  /** Voice-vs-text is decided PER RESPONSE: the session itself stays
   * audio-capable (switching session-level output_modalities mid-session
   * is unreliable), and every response we request carries the current
   * preference + the text-only-while-recording rule. */
  createResponse() {
    this.sendClientEvent({
      type: 'response.create',
      response: {
        output_modalities: this.voiceRepliesActive() ? ['audio'] : ['text'],
      },
    });
  }

  cancelResponse() {
    this.sendClientEvent({ type: 'response.cancel' });
  }

  sendClientEvent(message) {
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      message.event_id = message.event_id || crypto.randomUUID();
      this.dataChannel.send(JSON.stringify(message));
    } else {
      console.warn(
        'AgentRuntime: data channel not open, dropped event',
        message.type,
      );
    }
  }

  /* ===== Tools and context (Dynamic MCP pattern, same as Synapse) ===== */

  addTools(tools) {
    (Array.isArray(tools) ? tools : [tools]).forEach((tool) =>
      this.toolsRegistry.addTool(tool),
    );
    this.pushSessionUpdate();
  }

  removeTools(toolNames) {
    (Array.isArray(toolNames) ? toolNames : [toolNames]).forEach((name) =>
      this.toolsRegistry.removeTool(name),
    );
    this.pushSessionUpdate();
  }

  addContext(contextId, contextData) {
    this.contextRegistry.addContext(contextId, contextData);
    this.pushSessionUpdate();
  }

  removeContext(contextId) {
    this.contextRegistry.removeContext(contextId);
    this.pushSessionUpdate();
  }

  pushSessionUpdate() {
    if (this.status === 'ready' || this.status === 'listening') {
      this.sendClientEvent(this.getSessionUpdatePayload());
    }
  }

  getSessionUpdatePayload() {
    const instructions =
      this.systemDirective + this.contextRegistry.getFormattedContext();
    return {
      type: 'session.update',
      session: {
        type: 'realtime',
        model: this.model,
        // Always audio-capable; per-response output_modalities does the
        // actual voice/text gating (see createResponse).
        output_modalities: ['audio'],
        audio: {
          output: { voice: this.voice },
          input: {
            transcription: { model: 'whisper-1' },
            // We create responses ourselves so spoken questions also
            // respect the voice-replies toggle / recording rule.
            turn_detection: { type: 'server_vad', create_response: false },
          },
        },
        instructions,
        tools: this.toolsRegistry.getAllTools(),
        tool_choice: 'auto',
      },
    };
  }

  /* ===== Server events ===== */

  async processIncomingEvent(event) {
    switch (event.type) {
      // Streamed assistant text (text-only replies)
      case 'response.output_text.delta':
      // Transcript of spoken replies — same chat bubble either way
      case 'response.output_audio_transcript.delta': {
        const id = event.response_id || 'current';
        const sofar =
          (this.pendingResponses.get(id) || '') + (event.delta || '');
        this.pendingResponses.set(id, sofar);
        this.events.emit('assistant_delta', { responseId: id, text: sofar });
        break;
      }

      // What the user said (mic input), once transcribed
      case 'conversation.item.input_audio_transcription.completed': {
        if (event.transcript) {
          this.events.emit('user_transcript', {
            text: event.transcript,
            itemId: event.item_id,
          });
        }
        break;
      }

      // Server VAD committed a spoken turn — anchor the user's bubble NOW
      // (the transcript arrives later, often after the reply has started)
      // and issue the response so the modality preference applies.
      case 'input_audio_buffer.committed':
        this.events.emit('user_speech_committed', { itemId: event.item_id });
        this.createResponse();
        break;

      case 'output_audio_buffer.started':
        this.events.emit('speaking_change', true);
        break;
      case 'output_audio_buffer.stopped':
        this.events.emit('speaking_change', false);
        break;

      case 'response.done': {
        const id = event.response?.id || 'current';
        const text = this.pendingResponses.get(id) || '';
        this.pendingResponses.delete(id);
        if (text) {
          this.events.emit('assistant_done', { responseId: id, text });
        }
        if (event.response?.output) {
          for (const output of event.response.output) {
            if (output.type === 'function_call') {
              await this.executeToolCall(output);
            }
          }
        }
        break;
      }

      case 'error':
        console.error('AgentRuntime server error:', event);
        this.events.emit('error', event);
        break;

      default:
        break;
    }
  }

  async executeToolCall(output) {
    const tool = this.toolsRegistry.getTool(output.name);
    let result;
    if (tool && typeof tool.handler === 'function') {
      try {
        const args = output.arguments ? JSON.parse(output.arguments) : {};
        result = await tool.handler(args);
        this.events.emit('tool_call', { name: output.name, args, result });
      } catch (error) {
        console.error(`AgentRuntime tool "${output.name}" failed:`, error);
        result = { error: String(error?.message || error) };
      }
    } else {
      result = { error: `No handler registered for tool "${output.name}"` };
    }

    // Return the result so the model can speak/confirm it.
    if (output.call_id) {
      this.sendClientEvent({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: output.call_id,
          output: JSON.stringify(result ?? { ok: true }),
        },
      });
      this.createResponse();
    }
  }
}

export default AgentRuntime;
